import type mongoose from "mongoose"
import type { ClientSession } from "mongoose"

import { getGlobalDbName, getTenantDbName } from "./dbNames"
import { ensureMongooseConnection } from "./ensureMongooseConnection"
import { getTenantIdFromLoadModelCtx, type LoadModelCtx } from "./modelsApi"
import { getTenantFilesystemDbName } from "./tenantFilesystemDb"


type MongooseTransactionOptions = Parameters<ClientSession["withTransaction"]>[1]

export type TenantGlobalFilesystemTransactionCtx = {
  tenantId: string
  session: ClientSession
  ctx: {
    tenant: LoadModelCtx
    global: LoadModelCtx
  }
  db: {
    tenant: mongoose.Connection
    global: mongoose.Connection
    filesystem: mongoose.Connection
  }
}

type WithTransactionScope =
  | string
  | { tenantId: string }
  | { ctx: LoadModelCtx }

const buildTenantLoadModelCtx = (tenantId: string): LoadModelCtx => ({
  req: {
    session: {
      user: {
        currentTenantId: tenantId,
      },
    },
  },
})

export function withTransaction<T>(
  tenantId: string,
  fn: (ctx: TenantGlobalFilesystemTransactionCtx) => Promise<T>,
  options?: MongooseTransactionOptions,
): Promise<T>
export function withTransaction<T>(
  scope: { tenantId: string } | { ctx: LoadModelCtx },
  fn: (ctx: TenantGlobalFilesystemTransactionCtx) => Promise<T>,
  options?: MongooseTransactionOptions,
): Promise<T>
export async function withTransaction<T>(
  scope: WithTransactionScope,
  fn: (ctx: TenantGlobalFilesystemTransactionCtx) => Promise<T>,
  options?: MongooseTransactionOptions,
): Promise<T> {
  const normalizedTenantId = (() => {
    if (typeof scope === "string") return scope.trim()
    if ("tenantId" in scope) return scope.tenantId.trim()
    return getTenantIdFromLoadModelCtx(scope.ctx)
  })()

  if (!normalizedTenantId) throw new Error("Tenant ID is missing")

  const tenantDbName = getTenantDbName(normalizedTenantId)
  const globalDbName = getGlobalDbName()
  const filesystemDbName = getTenantFilesystemDbName(normalizedTenantId)

  const tenantDb = await ensureMongooseConnection(tenantDbName)
  const globalDb = await ensureMongooseConnection(globalDbName)
  const filesystemDb = await ensureMongooseConnection(filesystemDbName)

  const session = await tenantDb.startSession()

  const tenantCtx: LoadModelCtx = typeof scope === "object" && "ctx" in scope
    ? scope.ctx
    : buildTenantLoadModelCtx(normalizedTenantId)

  const globalCtx: LoadModelCtx = { req: { session: null } }

  try {
    return await session.withTransaction(async () => fn({
      tenantId: normalizedTenantId,
      session,
      ctx: {
        tenant: tenantCtx,
        global: globalCtx,
      },
      db: {
        tenant: tenantDb,
        global: globalDb,
        filesystem: filesystemDb,
      },
    }), options)
  } finally {
    await session.endSession()
  }
}
